// Stats tab — usage totals, 30-day daily sparkline, per-user
// leaderboard, top tool calls.

import { useEffect, useState } from "preact/hooks";
import { api, type UsageStats } from "../../lib/rpc";

interface StatsTabProps {
  notify: (msg: string) => void;
}

export function AdminStatsTab({ notify }: StatsTabProps) {
  const [stats, setStats] = useState<UsageStats | null>(null);
  const [sinceDays, setSinceDays] = useState<number>(30);

  useEffect(() => {
    let cancelled = false;
    api.adminUsageStats({ sinceDays: sinceDays > 0 ? sinceDays : undefined })
      .then((s) => { if (!cancelled) setStats(s); })
      .catch((e: any) => { if (!cancelled) { notify(e?.message || "Couldn't load stats"); setStats({ totals: { turns: 0, prompt_tokens: 0, completion_tokens: 0, tool_calls: 0 }, perUser: [], daily: [], topTools: [] }); } });
    return () => { cancelled = true; };
  }, [sinceDays, notify]);

  if (!stats) return <div className="admin-card"><p className="admin-loading">Loading…</p></div>;

  const sparkline = stats.daily.slice().reverse(); // server returns DESC; chart goes oldest → newest
  const maxTokens = Math.max(1, ...sparkline.map((d) => d.tokens));

  return (
    <>
      <div className="admin-card">
        <h3>Usage</h3>
        <p className="admin-card-sub">
          Totals + a daily sparkline of completion-token volume.
        </p>

        <div className="admin-audit-filters">
          <select
            className="input"
            value={sinceDays}
            onChange={(e) => setSinceDays(Number((e.currentTarget as HTMLSelectElement).value))}
          >
            <option value={1}>last 24h</option>
            <option value={7}>last 7 days</option>
            <option value={30}>last 30 days</option>
            <option value={0}>all time</option>
          </select>
        </div>

        <div className="admin-stat-grid">
          <Stat label="Turns"             value={fmt(stats.totals.turns)} />
          <Stat label="Prompt tokens"     value={fmt(stats.totals.prompt_tokens)} />
          <Stat label="Completion tokens" value={fmt(stats.totals.completion_tokens)} />
          <Stat label="Tool calls"        value={fmt(stats.totals.tool_calls)} />
        </div>

        {sparkline.length > 0 && (
          <>
            <div className="admin-sparkline">
              {sparkline.map((d, i) => (
                <div
                  key={d.day}
                  className="admin-sparkline-bar"
                  data-current={i === sparkline.length - 1}
                  style={{ height: `${(d.tokens / maxTokens) * 100}%` }}
                  title={`${d.day} · ${fmt(d.tokens)} tokens · ${d.turns} turns`}
                />
              ))}
            </div>
            <div className="admin-sparkline-axis">
              <span>{sparkline[0]?.day}</span>
              <span>{sparkline[sparkline.length - 1]?.day}</span>
            </div>
          </>
        )}
      </div>

      <div className="admin-card">
        <h3>Per-user</h3>
        <p className="admin-card-sub">Top contributors over the selected window.</p>
        {stats.perUser.length === 0 ? (
          <p className="admin-empty">No activity in this window.</p>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Turns</th>
                <th>Prompt</th>
                <th>Completion</th>
              </tr>
            </thead>
            <tbody>
              {stats.perUser.map((u) => (
                <tr key={u.user_id}>
                  <td className="admin-user-mono">{u.user_id}</td>
                  <td>{fmt(u.turns)}</td>
                  <td>{fmt(u.prompt_tokens)}</td>
                  <td>{fmt(u.completion_tokens)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="admin-card">
        <h3>Top tools</h3>
        <p className="admin-card-sub">Most-called tools in this window.</p>
        {stats.topTools.length === 0 ? (
          <p className="admin-empty">No tool calls in this window.</p>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Tool</th>
                <th style={{ width: 80, textAlign: "right" }}>Calls</th>
              </tr>
            </thead>
            <tbody>
              {stats.topTools.map((t) => (
                <tr key={t.tool_name}>
                  <td className="admin-user-mono">{t.tool_name}</td>
                  <td style={{ textAlign: "right" }}>{fmt(t.n)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="admin-stat">
      <div className="admin-stat-label">{label}</div>
      <div className="admin-stat-value">{value}</div>
    </div>
  );
}

function fmt(n: number): string {
  if (!Number.isFinite(n)) return "—";
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(2) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1) + "k";
  return String(n);
}
